/**
 * Custom request submitted confirmation page.
 * Uses the same two-column layout as the instant booking confirmation.
 * Left column: Need to Know + Download App. Right column: Request summary.
 *
 * All request data is read from a per-UUID localStorage snapshot (not widget state)
 * so it's immune to other tabs/windows overwriting the shared widget atom.
 *
 * @module Widget/CustomRequest/CustomRequestSubmittedPage
 */

import React, { Suspense, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import * as Sentry from '@sentry/react';
import { ApiError } from '@/lib/ApiError.ts';
import {
  RestaurantAtom,
  AuthAtom,
  updateWidgetStateAtom,
  useResetState,
  useRestaurantId,
} from '../lib/state.ts';
import { reset as resetAnalytics } from '@/lib/analytics/index.ts';
import {
  getRestaurant,
  getCustomRequestSettings,
  getUserReservation,
  getReservationMetadata,
  cancelReservationConfirm,
  cancelReservationFinish,
  cancelReservationConfirmAuth,
  cancelReservationAuth,
} from '@/lib/api.ts';
import type { ReservationMetadataResponse } from '@/lib/api.ts';
import { getPhone } from '@/lib/getPhone.ts';
import type { GetReservation, RestaurantResponseData } from '@/lib/types.ts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(tz);
import NeedToKnows from '@/components/NeedToKnows/NeedToKnows.tsx';
import MapView from '@/components/MapView/MapView.tsx';
import DorsiaAppIcon from '@/assets/images/App_Icon.png';
import AppStoreBadge from '@/assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg?react';
import { formatTimeRange, loadRequestSnapshot } from './utils.ts';
import Card, { type CardNames } from '@/components/Card/Card.tsx';
import cs from '../Confirmed/ConfirmedPage.module.css';
import styles from './CustomRequest.module.css';

/**
 * Confirmation page displayed after a custom request is submitted.
 * Two-column layout mirroring the instant booking confirmation.
 *
 * @returns Rendered submitted page
 */
const CustomRequestSubmittedPage: React.FC = () => {
  const { uuid } = useParams<{ uuid?: string }>();
  const id = useRestaurantId();
  const storedRestaurant = useAtomValue(RestaurantAtom);
  const auth = useAtomValue(AuthAtom);
  const setAuth = useSetAtom(AuthAtom);
  const resetState = useResetState();
  const navigate = useNavigate();
  const hasValidUuid = uuid && uuid.length >= 10;
  const [needsAuth, setNeedsAuth] = useState(!!hasValidUuid && !auth?.token);

  // A submitted custom request is a terminal state, like a booking confirmation:
  // mark the embed session completed so a later overlay reopen starts fresh rather
  // than resuming this finished request (shouldResetEmbedSession rule (b)). Writes
  // only to the in-memory embed session — never partner-origin storage. Runs once.
  const markWidgetCompleted = useSetAtom(updateWidgetStateAtom);
  const completedMarked = useRef(false);
  useEffect(() => {
    if (!completedMarked.current && hasValidUuid) {
      completedMarked.current = true;
      markWidgetCompleted({ completed: true });
    }
  }, [hasValidUuid, markWidgetCompleted]);

  const [metadata, setMetadata] = useState<ReservationMetadataResponse | null>(null);
  useEffect(() => {
    if (!hasValidUuid || !needsAuth) return;
    void getReservationMetadata(uuid).then(setMetadata).catch(() => {});
  }, [uuid, hasValidUuid, needsAuth]);

  // Load request data from per-UUID snapshot (independent of shared widget state)
  const snapshot = useMemo(
    () => (hasValidUuid ? loadRequestSnapshot(uuid) : null),
    [uuid, hasValidUuid]
  );

  const [restaurant, setLocalRestaurant] = useState<RestaurantResponseData | null>(storedRestaurant);
  const [resolvedTableTypeNames, setResolvedTableTypeNames] = useState<string[]>(
    snapshot?.selected_table_type_names ?? []
  );

  useEffect(() => {
    if (storedRestaurant) {
      setLocalRestaurant(storedRestaurant);
      return;
    }
    if (!id) return;
    const date = snapshot?.date ?? new Date().toISOString().slice(0, 10);
    void getRestaurant(id, date, '0:00', 'submitted', auth?.token ?? null).then((res) => {
      if (res?.data) setLocalRestaurant(res.data);
    });
  }, [id, storedRestaurant, auth?.token]);

  useEffect(() => {
    if (resolvedTableTypeNames.length > 0 || !snapshot?.selected_table_types?.length || !id) return;
    void getCustomRequestSettings(id, auth?.token ?? null).then((res) => {
      const opts = res?.data?.customRequestSettings?.table_options ?? [];
      if (opts.length > 0 && snapshot.selected_table_types.length > 0) {
        const names = snapshot.selected_table_types.map((tableId) => {
          const match = opts.find((o) => Number(o.id) === Number(tableId));
          return match?.name ?? `Type ${tableId}`;
        });
        setResolvedTableTypeNames(names);
      }
    });
  }, [id, auth?.token, snapshot?.selected_table_types, resolvedTableTypeNames.length]);

  const currencySymbol = restaurant?.currency_symbol ?? '$';

  type RequestStatus = 'pending' | 'confirmed' | 'cancelled' | 'denied' | 'expired' | 'unknown';

  const [apiReservation, setApiReservation] = useState<GetReservation | null>(null);

  // User info from snapshot or API response
  const userName = snapshot
    ? [snapshot.user_first_name, snapshot.user_last_name].filter(Boolean).join(' ')
    : apiReservation
      ? [apiReservation.user?.first_name, apiReservation.user?.last_name].filter(Boolean).join(' ')
      : '';
  const userPhone = getPhone(snapshot?.user_phone ?? apiReservation?.user?.phone);
  const userEmail = snapshot?.user_email ?? apiReservation?.user?.email;
  const hasUserInfo = !!userName;

  // Payment info from snapshot or API response
  const paymentBrand = snapshot?.payment_brand ?? apiReservation?.card?.brand;
  const paymentLast4 = snapshot?.payment_last4 ?? apiReservation?.card?.number;
  const hasPayment = !!paymentBrand && !!paymentLast4;
  const [requestStatus, setRequestStatus] = useState<RequestStatus>('unknown');
  const [signature, setSignature] = useState<string | null>(null);
  const [confirmationDeadline, setConfirmationDeadline] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const statusLoading = requestStatus === 'unknown';
  const isPending = requestStatus === 'pending';
  const cancelled = requestStatus === 'cancelled';

  // Fetch reservation to determine current status and get signature for cancel.
  // If confirmed, redirect to the full confirmation page with final details.
  useEffect(() => {
    if (!auth?.token || !uuid) return;
    void getUserReservation(auth.token, uuid).then((res) => {
      const r = res?.reservation;
      if (!r) return;

      setApiReservation(r);
      if (r.hash_signature) setSignature(r.hash_signature);
      if (r.confirmation_deadline_at) setConfirmationDeadline(r.confirmation_deadline_at);

      if (r.cancelled) {
        setRequestStatus('cancelled');
      } else if (r.denied_at) {
        setRequestStatus('denied');
      } else if (r.confirmed_at && r.confirmation_code) {
        navigate(`/restaurant/${id}/confirmed/${uuid}`, { replace: true });
        return;
      } else if (r.failed_at || r.is_past) {
        setRequestStatus('expired');
      } else if (r.proposed_time_frame) {
        setRequestStatus('pending');
      } else {
        setRequestStatus('unknown');
      }
    }).catch((e: unknown) => {
      if (e instanceof ApiError && e.type === 'UnauthorizedErrorResponse') {
        setNeedsAuth(true);
      } else {
        Sentry.captureException(e);
        setRequestStatus('pending');
      }
    });
  }, [auth?.token, uuid, id, navigate]);

  const handleCancelRequest = useCallback(async () => {
    if (!uuid) return;
    setCancelling(true);
    setCancelError(null);

    try {
      let cancelType: string | undefined;
      let success = false;

      // Strategy 1: Authenticated cancel (uses Sanctum token)
      if (auth?.token) {
        const confirm = await cancelReservationConfirmAuth(auth.token, uuid);
        cancelType = confirm?.type;

        if (cancelType) {
          const result = await cancelReservationAuth(auth.token, uuid, { type: cancelType });
          if (result?.reservation) {
            success = true;
          }
        }
      }

      // Strategy 2: Signature-based public cancel (fallback)
      if (!success && signature) {
        const confirm = await cancelReservationConfirm(signature, uuid);
        cancelType = confirm?.type;

        if (cancelType) {
          const result = await cancelReservationFinish(signature, uuid, { type: cancelType });
          if (result?.reservation) {
            success = true;
          }
        }
      }

      if (!success) {
        throw new Error(
          'Unable to cancel this request. The restaurant may need to handle this directly. Please contact support if the issue persists.'
        );
      }

      setRequestStatus('cancelled');
      setCancelModalOpen(false);
    } catch (e) {
      Sentry.captureException(e);
      const message = e instanceof Error ? e.message : 'Failed to cancel request.';
      setCancelError(message);
    } finally {
      setCancelling(false);
    }
  }, [uuid, auth?.token, signature]);

  const restPhone = getPhone(restaurant?.phone);
  const flatAddress = restaurant?.address ? restaurant.address.replace(/[\r\n]+/g, ', ') : '';

  const onRestart = () => {
    resetState();
    navigate(`/restaurant/${id}/booking`);
  };

  if (!hasValidUuid) {
    return (
      <div className={cs.eventContent}>
        <div className="flex flex-col items-center gap-4 bg-white rounded-[20px] p-10 text-center">
          <h2 className={cs.sectionHeadingMedium}>Something Went Wrong</h2>
          <p className={cs.orderSummaryAddress}>
            Your request may not have been submitted successfully.
            Please go back and try again, or contact support if the issue persists.
          </p>
          <button className={cs.secondaryButton} onClick={onRestart}>
            Back to Restaurant
          </button>
        </div>
      </div>
    );
  }

  if (needsAuth) {
    return (
      <div className={cs.eventContent}>
        <div className="flex flex-col items-center gap-4 bg-white rounded-[20px] p-10 text-center">
          <h2 className={cs.sectionHeadingMedium}>
            Verify your identity to view your request.
          </h2>
          {metadata && metadata.restaurant_name && (
            <div className="text-sm text-gray-500 space-y-1">
              <p className="font-medium text-gray-700">{metadata.restaurant_name}</p>
              {metadata.date && <p>{metadata.date}{metadata.time ? ` at ${metadata.time}` : ''}</p>}
              {metadata.party_size && (
                <p>{metadata.party_size} guest{metadata.party_size !== 1 ? 's' : ''}</p>
              )}
            </div>
          )}
          <p className="text-sm text-gray-500 mt-2 mb-5">
            Please verify your phone number to access your request details.
          </p>
          <button
            onClick={() => {
              setAuth(null);
              // Drop the stale Segment identity too, so the re-verification
              // funnel that follows isn't attributed to the previous member.
              resetAnalytics();
              navigate(`../details?returnTo=request/submitted/${uuid}`);
            }}
            className={cs.primaryButton}
          >
            Verify Phone Number
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero heading */}
      <h1 className={cs.confirmationHero}>
        {statusLoading ? 'Loading...'
          : requestStatus === 'cancelled' ? 'Request cancelled'
          : requestStatus === 'denied' ? 'Request declined'
          : requestStatus === 'confirmed' ? 'Request confirmed'
          : requestStatus === 'expired' ? 'Request expired'
          : 'Request submitted'}
      </h1>

      {/* Two-column layout */}
      <div className={cs.twoColumnLayout}>
        {/* Left column: Status + Need to Know + Download App */}
        <div className={cs.mainColumn}>
          {/* Status banner */}
          {!statusLoading && (
            <div className="flex gap-5 items-start rounded-[20px] bg-white p-5">
              {requestStatus === 'cancelled' || requestStatus === 'denied' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
                  <circle cx="12" cy="12" r="8" stroke="#e63535" strokeWidth="1.2" />
                  <path d="M9 9l6 6M15 9l-6 6" stroke="#e63535" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              ) : requestStatus === 'confirmed' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
                  <circle cx="12" cy="12" r="8" stroke="#22c55e" strokeWidth="1.2" />
                  <path d="M9 12l2 2 4-4" stroke="#22c55e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : requestStatus === 'expired' ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
                  <circle cx="12" cy="12" r="8" stroke="#8d8d8d" strokeWidth="1.2" />
                  <path d="M12 8v4l2.5 2.5" stroke="#8d8d8d" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
                  <circle cx="12" cy="12" r="8" stroke="#f59e0b" strokeWidth="1.2" />
                  <path d="M12 8v4l2.5 2.5" stroke="#f59e0b" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              )}
              <div className="flex flex-col gap-2 flex-1">
                <p className="font-['Sailec',sans-serif] font-medium text-xl leading-tight text-black m-0">
                  {requestStatus === 'cancelled' ? 'Request Cancelled'
                    : requestStatus === 'denied' ? 'Request Declined'
                    : requestStatus === 'confirmed' ? 'Request Confirmed'
                    : requestStatus === 'expired' ? 'Request Expired'
                    : 'Pending Restaurant Response'}
                </p>
                <p className="font-['Sailec',sans-serif] text-[15px] leading-normal text-black m-0">
                  {requestStatus === 'cancelled'
                    ? 'Your custom request has been cancelled. The pre-authorization hold on your payment method will be released.'
                    : requestStatus === 'denied'
                    ? `Your custom request at ${restaurant?.name ?? 'the restaurant'} was declined by the restaurant. The pre-authorization hold on your payment method will be released.`
                    : requestStatus === 'confirmed'
                    ? `Your custom request at ${restaurant?.name ?? 'the restaurant'} has been confirmed! Check the Dorsia app for details.`
                    : requestStatus === 'expired'
                    ? 'This custom request has expired without a response from the restaurant. The pre-authorization hold on your payment method will be released.'
                    : `Your custom request has been sent to ${restaurant?.name ?? 'the restaurant'}. You'll be notified once they respond.`}
                </p>
              </div>
            </div>
          )}

          {/* Cancel request — prominent placement when pending */}
          {isPending && !cancelled && (
            <button
              className="w-full h-12 rounded-[10px] bg-red-50 font-['Sailec',sans-serif] text-[15px] font-medium text-red-500 border-none cursor-pointer transition-colors hover:bg-red-100 mt-3"
              onClick={() => setCancelModalOpen(true)}
            >
              Cancel Pending Request
            </button>
          )}

          {/* Need to know */}
          {restaurant?.need_to_knows && restaurant.need_to_knows.length > 0 && (
            <div className="bg-white rounded-[20px] p-5 mt-5">
              <div className={cs.needToKnow}>
                <p className={cs.sectionHeadingMedium}>Need to know</p>
                <Suspense fallback={<div>Loading...</div>}>
                  <NeedToKnows need_to_knows={restaurant.need_to_knows} />
                </Suspense>
              </div>
            </div>
          )}

          {/* Download App card */}
          <a
            className={`${cs.downloadSection} mt-5`}
            href="https://apps.apple.com/us/app/dorsia-members-club/id1589267208"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={DorsiaAppIcon} alt="Dorsia App" className={cs.downloadIcon} />
            <h3 className={cs.downloadTitle}>Download the Dorsia App</h3>
            <p className={cs.downloadText}>
              Download the Dorsia app to manage your reservations, view your dining history, and
              discover new restaurants.
            </p>
            <AppStoreBadge className={cs.appStoreButton} />
          </a>

          {/* Back to restaurant */}
          <button className={`${cs.secondaryButton} mt-4`} onClick={onRestart}>
            Back to Restaurant
          </button>

          {/* Bottom spacer */}
          <div className="pb-8" />
        </div>

        {/* Cancel confirmation modal */}
        {cancelModalOpen && (
          <div
            className={styles.modalOverlay}
            onClick={() => !cancelling && setCancelModalOpen(false)}
          >
            <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <button
                className={styles.modalClose}
                onClick={() => !cancelling && setCancelModalOpen(false)}
                aria-label="Close"
              >
                &times;
              </button>

              <h2 className={styles.modalTitle}>Cancel Pending Request</h2>

              <p className={styles.modalText}>
                Are you sure you want to cancel this custom request at{' '}
                {restaurant?.name ?? 'this restaurant'}? This action cannot be undone.
              </p>

              <div className={styles.modalNotice}>
                <p className={styles.modalNoticeText}>
                  Since the restaurant hasn&apos;t responded yet, there will be no charge. The
                  pre-authorization hold on your payment method will be released.
                </p>
              </div>

              {cancelError && <p className={styles.modalError}>{cancelError}</p>}

              <div className={styles.modalActions}>
                <button
                  className={styles.modalBtnSecondary}
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelling}
                >
                  Close
                </button>
                <button
                  className={styles.modalBtnDanger}
                  onClick={() => void handleCancelRequest()}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Right column: Request summary */}
        <div className={cs.sidebarColumn}>
          <div className={cs.orderSummary}>
            {/* Restaurant name + pending badge */}
            <div className="flex justify-between items-start">
              <p className={cs.orderSummaryTitle}>{restaurant?.name ?? 'Loading...'}</p>
              {!statusLoading && (
                <span className={`font-['Sailec',sans-serif] text-[10px] font-medium leading-tight px-2 py-1 rounded-full shrink-0 ml-2 text-white ${
                  requestStatus === 'cancelled' || requestStatus === 'denied' ? 'bg-red-500'
                    : requestStatus === 'confirmed' ? 'bg-green-500'
                    : requestStatus === 'expired' ? 'bg-gray-400'
                    : 'bg-amber-400'
                }`}>
                  {requestStatus === 'cancelled' ? 'Cancelled'
                    : requestStatus === 'denied' ? 'Declined'
                    : requestStatus === 'confirmed' ? 'Confirmed'
                    : requestStatus === 'expired' ? 'Expired'
                    : 'Pending'}
                </span>
              )}
            </div>

            {restaurant?.address && (
              <p className={cs.orderSummaryAddress}>{restaurant.address}</p>
            )}

            {/* Member details */}
            {hasUserInfo && (
              <>
                <div className={cs.orderSummaryDivider} />
                <div className={cs.contactColumns}>
                  <div className={cs.contactColumn}>
                    <p className={cs.contactLabel}>MEMBER DETAILS</p>
                    <p className={cs.contactValue}>{userName}</p>
                    <p className={cs.contactValue}>
                      {userPhone ? userPhone.formatNational() : snapshot?.user_phone}
                    </p>
                    {userEmail && <p className={cs.contactValue}>{userEmail}</p>}
                  </div>
                </div>
              </>
            )}

            <div className={cs.orderSummaryDivider} />

            {/* Request details — prefer localStorage snapshot, fall back to API data */}
            {(snapshot || apiReservation) && (
              <>
                <div className={cs.orderSummaryPriceRow}>
                  <p className={cs.orderSummaryPriceLabel}>Date</p>
                  <p className={cs.orderSummaryPriceValue}>
                    {snapshot
                      ? dayjs(snapshot.date).format('ddd, MMMM D')
                      : apiReservation?.reservation_date_with_day_of_week
                        ?? apiReservation?.reservation_date_pretty
                        ?? (apiReservation?.date ? dayjs(apiReservation.date).format('ddd, MMMM D') : '—')}
                  </p>
                </div>
                <div className={cs.orderSummaryPriceRow}>
                  <p className={cs.orderSummaryPriceLabel}>Time Frame</p>
                  <p className={cs.orderSummaryPriceValue}>
                    {snapshot
                      ? formatTimeRange(snapshot.selected_times)
                      : apiReservation?.proposed_time_frame ?? '—'}
                  </p>
                </div>

                {snapshot && snapshot.selected_table_types.length > 0 ? (
                  <div className={cs.orderSummaryPriceRow}>
                    <p className={cs.orderSummaryPriceLabel}>Table Type</p>
                    <p className={`${cs.orderSummaryPriceValue} max-w-[60%] truncate`} title={
                      resolvedTableTypeNames.length > 0
                        ? resolvedTableTypeNames.join(', ')
                        : snapshot.selected_table_types.join(', ')
                    }>
                      {resolvedTableTypeNames.length > 0
                        ? resolvedTableTypeNames.join(', ')
                        : snapshot.selected_table_types.join(', ')}
                    </p>
                  </div>
                ) : !snapshot && apiReservation?.table_type ? (
                  <div className={cs.orderSummaryPriceRow}>
                    <p className={cs.orderSummaryPriceLabel}>Table Type</p>
                    <p className={`${cs.orderSummaryPriceValue} max-w-[60%] truncate`} title={apiReservation.table_type}>
                      {apiReservation.table_type}
                    </p>
                  </div>
                ) : null}

                <div className={cs.orderSummaryPriceRow}>
                  <p className={cs.orderSummaryPriceLabel}>Request Expires</p>
                  <p className={cs.orderSummaryPriceValue}>
                    {confirmationDeadline
                      ? dayjs.utc(confirmationDeadline).tz(restaurant?.timezone ?? 'America/New_York').format('dddd, MMMM D h:mmA')
                      : snapshot?.expiration_hours
                        ? `${snapshot.expiration_hours} hours`
                        : 'No limit'}
                  </p>
                </div>

                <div className={cs.orderSummaryDivider} />

                {(() => {
                  const partySize = snapshot?.party_size ?? apiReservation?.party_size;
                  const perPersonSpend = snapshot?.per_person_spend ?? apiReservation?.price_per_person;
                  return partySize != null && perPersonSpend != null ? (
                    <>
                      <div className={cs.orderSummaryPriceRow}>
                        <p className={cs.orderSummaryPriceLabel}>Party Size</p>
                        <p className={cs.orderSummaryPriceValue}>
                          {partySize} guest{partySize !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className={cs.orderSummaryPriceRow}>
                        <p className={cs.orderSummaryPriceLabel}>Min. Spend / Person</p>
                        <p className={cs.orderSummaryPriceValue}>
                          {currencySymbol}{perPersonSpend}
                        </p>
                      </div>

                      <div className={cs.orderSummaryDivider} />

                      <div className={cs.orderSummaryTotal}>
                        <p className={cs.orderSummaryTotalLabel}>Total Minimum Spend</p>
                        <p className={cs.orderSummaryTotalValue}>
                          {currencySymbol}{perPersonSpend * partySize}
                        </p>
                      </div>
                    </>
                  ) : null;
                })()}

                {/* Payment method */}
                {hasPayment && (
                  <>
                    <div className={cs.orderSummaryDivider} />
                    <div className="flex items-center gap-3">
                      <div className="w-8 flex-shrink-0">
                        <Card name={(paymentBrand?.toLowerCase() ?? 'visa') as CardNames} className="" />
                      </div>
                      <p className={cs.orderSummaryPriceLabel}>
                        ending in {paymentLast4}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Map */}
            <div className="pt-3" />
            {restaurant?.address && <MapView restaurant={restaurant} height={200} />}

            {/* Directions / Phone / Website buttons */}
            <div className="flex flex-col gap-1.5">
              {restaurant?.address && (
                <a
                  className={cs.primaryButton}
                  href={`https://maps.google.com/?q=${encodeURIComponent(flatAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Directions
                </a>
              )}
              {restPhone && (
                <a
                  href={restPhone.getURI()}
                  className={cs.primaryButton}
                >
                  Call Restaurant
                </a>
              )}
              {restaurant?.website && (
                <a
                  href={restaurant.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cs.primaryButton}
                >
                  Website
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomRequestSubmittedPage;
